import React from 'react';
import { Modal } from './Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faBullseye, faDumbbell, faBolt, faMapPin, faCircle, faStar, faSliders, faFutbol, faShield, faBrain, faLightbulb, faCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import './HelpModal.css';

export const TeamSetupHelpModal: React.FC<{ isOpen: boolean; onClose: () => void; }> = ({ isOpen, onClose }) => (
    <Modal isOpen={isOpen} onClose={onClose} className="help-modal">
        <h2><FontAwesomeIcon icon={faUsers} style={{ color: 'var(--accent-color)', marginRight: '8px' }} /> Team Setup Guide</h2>
        <p style={{ fontSize: '1.1em', marginBottom: '24px' }}>Your team selection is EVERYTHING! ⚡ The right players in the right positions with the right fitness level can turn a draw into a win. Master this and you'll dominate.</p>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faBullseye} style={{ color: 'var(--accent-color)', marginRight: '8px' }} /> How Performance is Calculated</h3>
            <div style={{ backgroundColor: 'var(--primary-color)', padding: '12px', borderRadius: '4px', marginTop: '8px' }}>
                <p style={{ margin: '0 0 12px 0', fontWeight: 'bold' }}>Performance = Skill × Fitness × Form × Position Match</p>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    <li><strong>Skill (SKL):</strong> Player's base ability in that position</li>
                    <li><strong>Fitness:</strong> Current stamina (0-100%). A tired player underperforms significantly</li>
                    <li><strong>Form:</strong> Recent performance bonus/penalty (-5 to +5)</li>
                    <li><strong>Position Match:</strong> How natural the player is in that position</li>
                </ul>
            </div>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faDumbbell} style={{ color: 'var(--accent-color)', marginRight: '8px' }} /> Understanding Player Attributes</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong>SKL (Skill)</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px', lineHeight: '1.4' }}>
                        Current ability<br />
                        in this position<br />
                        <span style={{ fontSize: '0.85em', color: 'var(--accent-text-color)' }}>Higher = better</span>
                    </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong>POT (Potential)</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px', lineHeight: '1.4' }}>
                        Star rating of their<br />
                        future ability<br />
                        <span style={{ fontSize: '0.85em', color: 'var(--accent-text-color)' }}>Young talent = invest</span>
                    </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong>AGE</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px', lineHeight: '1.4' }}>
                        Player's age<br />
                        <span style={{ fontSize: '0.85em', color: 'var(--accent-text-color)' }}>Young: develop, Old: sell</span>
                    </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong>FORM</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px', lineHeight: '1.4' }}>
                        Recent performance<br />
                        (-5 to +5)<br />
                        <span style={{ fontSize: '0.85em', color: 'var(--accent-text-color)' }}>High form: pick them</span>
                    </div>
                </div>
            </div>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faBolt} style={{ color: 'var(--accent-color)', marginRight: '8px' }} /> Fitness & Fatigue</h3>
            <p><strong>Critical for match performance!</strong></p>
            <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                <div style={{ flex: 1, padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong style={{ color: 'var(--positive-color)' }}>100% Fitness</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                        Full performance<br />
                        <span style={{ color: 'var(--info-color)' }}><FontAwesomeIcon icon={faCheck} /> Fresh & ready</span>
                    </div>
                </div>
                <div style={{ flex: 1, padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong style={{ color: 'var(--info-color)' }}>50% Fitness</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                        50% performance<br />
                        <span style={{ color: 'var(--accent-text-color)' }}><FontAwesomeIcon icon={faTriangleExclamation} /> Tired</span>
                    </div>
                </div>
                <div style={{ flex: 1, padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong style={{ color: 'var(--negative-color)' }}>25% Fitness</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                        25% performance<br />
                        <span style={{ color: 'var(--negative-color)' }}><FontAwesomeIcon icon={faTriangleExclamation} /> Avoid!</span>
                    </div>
                </div>
            </div>
            <p style={{ marginTop: '12px' }}>
                <strong>Key tip:</strong> Rest key players before important matches. An extra week of rest can be the difference between victory and defeat!
            </p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faMapPin} style={{ color: 'var(--accent-color)', marginRight: '8px' }} /> Position Penalties</h3>
            <p><strong>Playing a player out of position reduces effectiveness:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Same position:</strong> Full performance (100%)</li>
                <li><strong>Familiar position:</strong> Small penalty (85-95%)</li>
                <li><strong>Unfamiliar position:</strong> Big penalty (60-75%) ❌</li>
                <li><strong>Completely wrong role:</strong> Severe penalty (20-40%) ❌❌</li>
            </ul>
            <p className="help-tip" style={{ marginTop: '12px' }}><FontAwesomeIcon icon={faLightbulb} style={{ color: 'var(--warning-color)', marginRight: '4px' }} /> <em>Keep players in their natural positions for best results. A CB playing as ST will be much less effective!</em></p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faCircle} style={{ color: 'var(--negative-color)', marginRight: '8px' }} /> Injuries & Suspensions</h3>
            <p><strong>Unavailable players:</strong></p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li><strong>Red Badge (Severe Injury):</strong> Cannot play - must recover</li>
                <li><strong>Yellow Badge (Light Injury):</strong> Can play but at reduced performance (0.8x)</li>
                <li><strong><FontAwesomeIcon icon={faTriangleExclamation} style={{ color: 'var(--warning-color)' }} /> Red Card (Suspension):</strong> Cannot play next match</li>
            </ul>
            <p style={{ marginTop: '12px' }}>
                Keep an eye on the injury list and plan accordingly. Having a backup for key positions is crucial!
            </p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faStar} style={{ color: 'var(--accent-color)', marginRight: '8px' }} /> Using Selection Suggestions</h3>
            <p><strong>Let the game help you:</strong></p>
            <p style={{ marginTop: '8px' }}>
                Look for the "Selection Suggestions" section on this screen. It recommends:
            </p>
            <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                <li>Players in good form who deserve a start</li>
                <li>Fresh players with high fitness</li>
                <li>The best available option for each position</li>
            </ul>
            <p style={{ marginTop: '12px' }}>
                <strong>Tip:</strong> Hover over player ratings to see detailed performance breakdown!
            </p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faSliders} style={{ color: 'var(--accent-color)', marginRight: '8px' }} /> Lineup Management Tools</h3>
            <div style={{ backgroundColor: 'var(--primary-color)', padding: '12px', borderRadius: '4px', marginTop: '8px' }}>
                <p style={{ margin: '0 0 8px 0' }}>Use the buttons at the top of the Team Setup panel:</p>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                    <li><strong>Auto-Fill Best XI:</strong> Automatically selects the best players based on performance, fitness, and form</li>
                    <li><strong>Clear All:</strong> Remove all players - useful for a fresh start</li>
                </ul>
            </div>
            <p style={{ marginTop: '12px' }}>
                <strong>Tip:</strong> Click "Auto-Fill Best XI" if you're not sure - it uses intelligent analysis to pick your best possible team!
            </p>
        </div>

        <div className="help-section">
            <h3><FontAwesomeIcon icon={faFutbol} style={{ color: 'var(--accent-color)', marginRight: '8px' }} /> Understanding Team Strengths</h3>
            <p><strong>Your lineup creates four key ratings:</strong></p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong><FontAwesomeIcon icon={faFutbol} style={{ color: 'var(--info-color)' }} /> GK (Goalkeeper)</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                        Goalkeeper strength<br />
                        <span style={{ color: 'var(--accent-text-color)' }}>Affects saves</span>
                    </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong><FontAwesomeIcon icon={faShield} style={{ color: 'var(--info-color)' }} /> Defence</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                        Defender performance<br />
                        <span style={{ color: 'var(--accent-text-color)' }}>Affects goals conceded</span>
                    </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong><FontAwesomeIcon icon={faBrain} style={{ color: 'var(--info-color)' }} /> Midfield</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                        Midfielder strength<br />
                        <span style={{ color: 'var(--accent-text-color)' }}>Affects possession</span>
                    </div>
                </div>
                <div style={{ padding: '12px', backgroundColor: 'var(--primary-color)', borderRadius: '4px' }}>
                    <strong><FontAwesomeIcon icon={faBullseye} style={{ color: 'var(--info-color)' }} /> Attack</strong>
                    <div style={{ fontSize: '0.9em', marginTop: '8px' }}>
                        Forward strength<br />
                        <span style={{ color: 'var(--accent-text-color)' }}>Affects goals scored</span>
                    </div>
                </div>
            </div>
            <p style={{ marginTop: '12px' }}>
                <strong>Strategy:</strong> Strong attack but weak defense? Use defensive tactics. Weak attack? Go attacking with high press.
            </p>
        </div>

        <div className="help-section" style={{ backgroundColor: 'rgba(0, 255, 155, 0.1)', padding: '12px', borderRadius: '4px' }}>
            <h3 style={{ marginTop: 0 }}><FontAwesomeIcon icon={faLightbulb} style={{ color: 'var(--warning-color)', marginRight: '8px' }} /> Quick Tips</h3>
            <ul style={{ margin: '8px 0 0 20px', lineHeight: '1.6' }}>
                <li>Always check the <strong>Team Strengths</strong> card to see how strong your lineup is</li>
                <li>Rest key players before big matches - fitness matters more than you think!</li>
                <li>Look at <strong>Selection Suggestions</strong> to spot in-form players</li>
                <li>Use <strong>Auto-Fill Best XI</strong> to quickly build a strong team</li>
                <li>Avoid playing players severely out of position (huge performance penalty)</li>
                <li>Keep backup options for every position in case of injuries</li>
                <li>A rested 80-skill player beats a tired 90-skill player!</li>
            </ul>
        </div>

        <div className="modal-actions">
            <button className="button primary" onClick={onClose}>Got it!</button>
        </div>
    </Modal>
);
